import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as React from 'react';
// @mui
import {
  Container,
  Typography,
  Stack,
  Button,
  IconButton
} from '@mui/material';
// Tabela
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Iconify from '../../../components/iconify';
import FilterField from '../components/FilterField';

// api
import { validateUser, deleteGenericData, updateGenericData, getKitEstoques } from '../../api';
import config from '../../../config.json';
import ModalLoading from '../../ModalLoading';
import ModalConfirmacao from '../../ModalConfirmacao';

import NewProdutoForm from '../NewProduto/NewProdutoForm';

// ----------------------------------------------------------------------

export default function Estoque() {
  const [propostas, setPropostas] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [isNewSaleModalOpen, setIsNewSaleModalOpen] = useState(false);
  const [produto, setProduto] = React.useState(null);
  const usuarioLogado = JSON.parse(localStorage.getItem('userData'));
  const [modalConfirmacaoDelete, setModalConfirmacaoDelete] = useState(false);
  const navigate = useNavigate();

  // Novo estado para o termo de busca
  const [searchTerm, setSearchTerm] = useState('');
  // Novo estado para os dados filtrados
  const [filteredPropostas, setFilteredPropostas] = useState([]);
  const unidadesEstoque = useMemo(() => [...new Map(propostas
    .flatMap((row) => row.estoquesPorUnidade || [])
    .map((unidade) => [unidade.chave, unidade])).values()], [propostas]);
  const saldoNaUnidade = (row, chave) => row.estoquesPorUnidade
    ?.find((unidade) => unidade.chave === chave)?.quantidade || 0;
  const formatarQuantidade = (valor) => Number(valor || 0).toLocaleString('pt-BR');


  // A variável filtro não será usada aqui diretamente para o filtro local
  // const filtro = {} // Remova ou comente esta linha se não for usar para a API

  useEffect(() => {
    validateuser();
  }, []);

  const validateuser = async () => {
    const teste = await validateUser();
    if (!teste.ok) {
      localStorage.removeItem('userData');
      localStorage.removeItem('userToken');
      config.user = {};
      navigate('/', { replace: true });
      window.alert('Sessão expirada, faça o login novamente');
      return;
    }
    localStorage.setItem('userData', JSON.stringify(teste.usuario));
    config.user = teste.usuario;
  };

  useEffect(() => {
    // Carrega todos os kits de estoque ao montar o componente
    // Se você tem muitos dados, pode ser melhor implementar o filtro na API
    fGetPropostas();
  }, []); // Vazio, para rodar apenas uma vez

  // Este useEffect reagirá a mudanças em 'propostas' ou 'searchTerm'
  useEffect(() => {
    const results = propostas.filter((row) => {
      const descricaoMatch = row.descricao
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const fornecedorMatch = row.fornecedor?.nome
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      return descricaoMatch || fornecedorMatch;
    });
    setFilteredPropostas(results);
  }, [searchTerm, propostas]); // Dependências: searchTerm e propostas

  const fGetPropostas = async () => { // Removi 'filtro' como parâmetro se o filtro for local
    setModalLoading(true);
    try {
      const result = await getKitEstoques({}); // Passa um objeto vazio ou o filtro inicial se tiver
      if (!Array.isArray(result)) throw new Error('Resposta de estoque inválida');
      // Compatibilidade durante deploys independentes de frontend e backend.
      setPropostas(result.map((row) => {
        if (Array.isArray(row.estoquesPorUnidade)) return row;
        const estoquesPorUnidade = [
          { chave: 'legado:1', nome: 'Araguari', quantidade: row.estoqueAraguari || 0 },
          { chave: 'legado:2', nome: 'Ribeirão Preto', quantidade: row.estoqueRibeirao || 0 },
        ];
        return { ...row, estoquesPorUnidade,
          estoqueTotal: estoquesPorUnidade.reduce((total, unidade) => total + unidade.quantidade, 0) };
      }));
    } catch (error) {
      window.alert('Erro ao consultar banco de dados');
      console.error('Erro ao buscar dados da API:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenNewSaleModal = () => {
    setIsNewSaleModalOpen(true);
  };

  const handleCloseNewSaleModal = () => {
    setIsNewSaleModalOpen(false);
    setProduto(null);
  };

  const handleDoubleClickTable = async (row) => {
    setProduto({ ...row, visualiza: true });
    setIsNewSaleModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setProduto({ ...row });
    setModalConfirmacaoDelete(true);
  };

  const closeModalConfirmacaDelete = () => {
    setModalConfirmacaoDelete(false);
    setProduto(null);
  };

  const confirmDelete = async () => {
    setModalLoading(true);
    await deleteGenericData('kitestoque', produto);
    closeModalConfirmacaDelete();
    fGetPropostas(); // Recarrega os dados após a exclusão
    setProduto(null);
    setModalLoading(false);
  };

  const onConfirmNewSale = async (data) => {
    await updateGenericData('kitestoque', data);
    fGetPropostas(); // Recarrega os dados após a adição/edição
    handleCloseNewSaleModal();
  };

  // ----------------------

  return (
    <>
      <Helmet>
        <title> Estoque </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4">Estoque</Typography>
          <Button
            variant="contained"
            onClick={handleOpenNewSaleModal}
            startIcon={<Iconify icon="zondicons:add-solid" />}
            sx={{ height: 50, minWidth: 100, marginLeft: 1 }}
          >
            Adicionar
          </Button>
        </Stack>

        {/* Campo de texto para filtro */}
        <FilterField setSearchTerm={setSearchTerm} searchTerm={searchTerm} label={"Filtrar por Descrição ou Fornecedor"}/>

        {/* ------------ Tabela ------------ */}
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">Nome</TableCell>
                <TableCell align="left">Fornecedor</TableCell>
                {unidadesEstoque.map((unidade) => (
                  <TableCell key={unidade.chave} align="center">{unidade.nome}</TableCell>
                ))}
                <TableCell align="center">Saldo total</TableCell>
                {usuarioLogado.supervisor && <TableCell align="center"> </TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredPropostas.map((row, index) => ( // Use filteredPropostas aqui
                <TableRow
                  key={index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: 'Azure' },
                    ...(row.finalizado?.nome === 'Rejeitado' && {
                      color: 'red',
                      textDecoration: 'line-through',
                    }),
                  }}
                  onDoubleClick={() => handleDoubleClickTable(row)}
                >
                  <TableCell align="left">{row.descricao}</TableCell>
                  <TableCell align="left">{row.fornecedor?.nome}</TableCell>
                  {unidadesEstoque.map((unidade) => (
                    <TableCell key={unidade.chave} align="center"
                      sx={{ color: saldoNaUnidade(row, unidade.chave) < 0 ? 'error.main' : 'inherit' }}>
                      {formatarQuantidade(saldoNaUnidade(row, unidade.chave))}
                    </TableCell>
                  ))}
                  <TableCell align="center">{formatarQuantidade(row.estoqueTotal)}</TableCell>
                  {/* Ícone de exclusão visível apenas para supervisores */}
                  {usuarioLogado.supervisor && (
                    <TableCell align="center">
                      <IconButton onClick={() => handleDeleteClick(row)}>
                        <Iconify icon="eva:trash-2-outline" width={24} height={24} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filteredPropostas.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={3 + unidadesEstoque.length + (usuarioLogado.supervisor ? 1 : 0)}>
                    Nenhum produto encontrado.
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell align="left" colSpan={2}>
                  Produtos exibidos: <strong>{filteredPropostas.length}</strong>
                </TableCell>
                {unidadesEstoque.map((unidade) => (
                  <TableCell key={unidade.chave} align="center">
                    <strong>{formatarQuantidade(filteredPropostas.reduce(
                      (total, row) => total + saldoNaUnidade(row, unidade.chave), 0
                    ))}</strong>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <strong>{formatarQuantidade(filteredPropostas.reduce(
                    (total, row) => total + (row.estoqueTotal || 0), 0
                  ))}</strong>
                </TableCell>
                {usuarioLogado.supervisor && <TableCell />}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Modal Loading */}
      {modalLoading && <ModalLoading open={modalLoading} />}

      {modalConfirmacaoDelete ? (
        <ModalConfirmacao
          title={'Tem certeza que deseja excluir esse produto?'}
          subtitle={`${produto.descricao} - ${produto.fornecedor?.nome}`}
          open={modalConfirmacaoDelete}
          onClose={closeModalConfirmacaDelete}
          onConfirm={confirmDelete}
        />
      ) : null}

      {isNewSaleModalOpen && (
        <NewProdutoForm
          open={isNewSaleModalOpen}
          formData={produto}
          handleClose={handleCloseNewSaleModal}
          onConfirm={onConfirmNewSale}
        />
      )}
    </>
  );
}
